import React from 'react';

import { useFormikContext } from 'formik';

import SelectFromField from 'CommonComponents/SelectFromField';
import TextFormField from 'CommonComponents/TextFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

/**
 * Hours as the entry unit forces the hours display on — asking for entry in a
 * unit the interface never shows is incoherent. Derived rather than written
 * into form state, so browsing the Default unit dropdown cannot overwrite the
 * admin's stored preference.
 *
 * The invariant has no durable enforcement: no database constraint and no model
 * guard, only the FormRequest. Anything writing `time_keeping_settings` directly
 * should apply this rule itself rather than assume the stored row obeys it.
 */
export function effectiveShowHours(values) {
    return values.default_unit === 'hours' ? true : values.show_hours;
}

export default function TimeKeepingOptionsCard({ disabled }) {
    const { values } = useFormikContext();

    const hoursIsDefaultUnit = values.default_unit === 'hours';

    return (
        <div className="table__card">
            <div className="tw-flex tw-flex-col tw-gap-1 tw-mb-6">
                <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-0">{$t('Time keeping options')}</h2>
                <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">{$t('editable while the module is enabled')}</p>
            </div>

            <div className="tw-grid tw-grid-cols-1 tw-gap-y-6 md:tw-grid-cols-2 md:tw-gap-x-8">
                <SelectFromField label={$t('Default unit')} as="select" name="default_unit" disabled={disabled} horizontalLayout={true}>
                    <option value="days">{$t('Days')}</option>
                    <option value="hours">{$t('Hours')}</option>
                </SelectFromField>

                <SelectFromField label={$t('Increment')} as="select" name="increment" disabled={disabled} horizontalLayout={true}>
                    <option value="quarter_day">{$t('Quarter day')}</option>
                    <option value="half_day">{$t('Half day')}</option>
                    <option value="whole_day">{$t('Whole day')}</option>
                </SelectFromField>

                <SelectFromField label={$t('Week starts')} as="select" name="week_starts" disabled={disabled} horizontalLayout={true}>
                    <option value="monday">{$t('Monday')}</option>
                    <option value="sunday">{$t('Sunday')}</option>
                </SelectFromField>

                {/* Days stay the stored unit whatever this is set to. It drives the
                    hours shown alongside a day total, and — where hours are the entry
                    unit — the step a cell accepts. */}
                <TextFormField
                    label={$t('Hours in a working day')}
                    name="hours_in_day"
                    type="number"
                    step="0.25"
                    min="1"
                    max="24"
                    disabled={disabled}
                    horizontalLayout={true}
                />

                <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                    <div className="tw-col-span-1">
                        <label htmlFor="show_hours" className="tw-text-charcoal tw-text-sm tw-mb-0">
                            {$t('Hours display')}
                        </label>
                    </div>
                    <div className="tw-col-span-2">
                        <ToggleFormField
                            type="checkbox"
                            id="show_hours"
                            name="show_hours"
                            checked={effectiveShowHours(values)}
                            disabled={disabled || hoursIsDefaultUnit}
                            label={$t('Show hours alongside days')}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
